import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Brain, ArrowLeft, Mail } from "lucide-react";

export default function StudentAuth() {
  const [matricula, setMatricula] = useState("");
  const [step, setStep] = useState("matricula");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const requestOtp = async () => {
    if (!matricula.trim() || cooldown > 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/student/request-otp", { matricula });
      setEmail(data.email);
      setStep("otp");
      setCooldown(data.resend_after || 30);
      if (data.dev_code) {
        toast.info(`Código de prueba: ${data.dev_code}`, { description: data.dev_notice, duration: 10000 });
      } else {
        toast.success("Código enviado", { description: `Revisa ${data.email}` });
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 429 && detail?.resend_after) setCooldown(detail.resend_after);
      toast.error(formatError(detail));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/student/verify-otp", { matricula, code });
      login(data.token, data.user);
      toast.success("Acceso concedido");
      navigate("/alumno");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md sepp-fade">
        <button onClick={() => navigate("/")} className="mb-8 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-home">
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
          <div className="mb-6 flex h-11 w-11 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Brain className="h-5 w-5" />
          </div>
          <h1 className="font-display text-2xl font-bold">Acceso de alumno</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {step === "matricula"
              ? "Ingresa tu matrícula para recibir un código de acceso."
              : `Introduce el código de 6 dígitos enviado a tu correo institucional.`}
          </p>

          {step === "matricula" ? (
            <div className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="matricula">Matrícula</Label>
                <Input
                  id="matricula"
                  data-testid="login-matricula-input"
                  placeholder="ej. a01234567"
                  value={matricula}
                  onChange={(e) => setMatricula(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && requestOtp()}
                />
                {matricula && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Mail className="h-3 w-3" /> {matricula.trim().toLowerCase()}@centrodepsicoterapiacognitiva.com
                  </p>
                )}
              </div>
              <Button className="w-full" disabled={busy || !matricula.trim()} onClick={requestOtp} data-testid="login-submit-button">
                {busy ? "Enviando..." : "Enviar código"}
              </Button>
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              <p className="text-xs text-muted-foreground">{email}</p>
              <div className="flex justify-center" data-testid="otp-input-code">
                <InputOTP maxLength={6} value={code} onChange={setCode}>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button className="w-full" disabled={busy || code.length !== 6} onClick={verify} data-testid="otp-verify-button">
                {busy ? "Verificando..." : "Verificar y entrar"}
              </Button>
              <button
                className="w-full text-center text-xs text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
                onClick={requestOtp}
                disabled={busy || cooldown > 0}
                data-testid="otp-resend-button"
              >
                {cooldown > 0 ? `Reenviar código en ${cooldown}s` : "Reenviar código"}
              </button>
              <button className="w-full text-center text-xs text-muted-foreground hover:text-foreground" onClick={() => setStep("matricula")}>
                Cambiar matrícula
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
